import { usePaymentInfoByAgencyId } from "../../../../../queries/pricing";
import toast from "react-hot-toast";
import { useState } from "react";
import CommonModal from "../../ResetContext/components/DumpModal";
import UpgradePlanModal from "./UpgradePlanModal";
import { useUserInfo } from "../../../../../context/user";
import Downgrade from "./Downgrade";
import { useUsersContext } from "../../../../../context/usersContext";
import ContactToSupportModal from "../../../../../components/ContactToSupportModal";

const FeatureRow = ({ enabled = true, current = false, children }) => (
  <li
    className={`flex items-start gap-2 leading-snug ${
      enabled
        ? current
          ? "text-foreground font-medium"
          : "text-foreground/75"
        : "text-muted-foreground/60"
    }`}
  >
    <span
      className={`h-1 w-1 rounded-full mt-[0.5em] shrink-0 ${
        enabled
          ? current
            ? "bg-[hsl(var(--task-accent))]"
            : "bg-foreground/30"
          : "bg-border"
      }`}
    />
    <span className="min-w-0">{children}</span>
  </li>
);

const PricingPlanCard = ({ data, planType, setActiveTab, agency, setOpenCancelModal }) => {
  const { mutateAsync: getPaymentInfoByAgencyId } = usePaymentInfoByAgencyId();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentMethod_id, setPaymentMethodId] = useState(null);
  const userActiveLocation = useUserInfo()[4];
  const [openDowngradeModal, setDowngradeModal] = useState(false);
  const [openContactInfoModel, setOpenContactInfoModel] = useState(false);
  const { isHighestPlan } = useUsersContext()
  const isHighest = isHighestPlan(data?._id);
  const isCurrent = Boolean(data?.activePlan);
  // Guard against products with no configured amount (renders as £NaN otherwise).
  const rawAmount = parseInt(
    planType === "monthly" ? data?.monthly_amount : data?.yearly_amount,
  );

  const handleUpgradeClick = async (type = "Upgrade plan") => {
    setLoading(true);
    let response;
    try {
      response = await getPaymentInfoByAgencyId({
        agency_id: userActiveLocation?.agency_id,
        customer_id: userActiveLocation?.customer_id,
      });
    } catch (err) {
      response = null;
    }
    if (response?.data?.id) {
      setPaymentMethodId(response.data.id);
      type === "Change plan"
        ? setDowngradeModal(true)
        : setOpenConfirmModal(true);
    } else {
      toast.error("Please add a payment method before upgrading.");
      setActiveTab("Billing");
    }
    setLoading(false);
  };

  const handleCtaClick = () => {
    if (!data?.activePlan || !loading) {
      if (data?.is_free_saas) {
        setOpenCancelModal(true);
      } else if (isHighest && data?.activePlan) setOpenContactInfoModel(true);
      else {
        handleUpgradeClick(data?.buttonLabel);
      }
    }
  };

  return (
    <div
      className={`group relative p-5 flex flex-col ${
        isCurrent ? "bg-[hsl(var(--task-accent)/0.04)]" : "hover:bg-muted/40"
      }`}
    >
      {isCurrent && (
        <span
          className="absolute top-0 inset-x-0 h-[3px] bg-[hsl(var(--task-accent))]"
          aria-hidden
        />
      )}

      <div className="mb-5">
        <div className="flex items-center justify-between gap-2 mb-1.5 min-h-[18px]">
          <h3
            className={`text-[11px] font-bold uppercase tracking-[0.14em] ${
              isCurrent
                ? "text-[hsl(var(--task-accent))]"
                : "text-muted-foreground"
            }`}
          >
            {data?.plan?.planName}
          </h3>
          {isCurrent && (
            <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-[4px] border border-[hsl(var(--task-accent)/0.30)] text-[hsl(var(--task-accent))] text-[9px] font-bold uppercase tracking-[0.12em] leading-none">
              <span
                className="h-1 w-1 rounded-full bg-[hsl(var(--task-accent))] animate-pulse"
                aria-hidden
              />
              Current
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1">
          {data?.is_free_saas ? (
            <span className="font-serif tabular-nums text-3xl tracking-tight text-foreground">
              Free
            </span>
          ) : Number.isFinite(rawAmount) ? (
            <>
              <span className="font-serif tabular-nums text-3xl tracking-tight text-foreground">
                £{rawAmount / 100}
              </span>
              <span className="text-xs text-muted-foreground">
                {planType === "monthly" ? "/mo" : "/yr"}
              </span>
            </>
          ) : (
            <span className="font-serif tabular-nums text-3xl tracking-tight text-muted-foreground">
              —
            </span>
          )}
        </div>
      </div>

      <ul className="space-y-2 mb-6 grow text-[12px]">
        <FeatureRow current={isCurrent}>AI Agents</FeatureRow>
        <FeatureRow enabled={parseInt(data?.user_limit) > 0} current={isCurrent}>
          {parseInt(data?.user_limit) > 0 && parseInt(data?.user_limit)} Users
        </FeatureRow>
        <FeatureRow enabled={Boolean(data?.create_agents)} current={isCurrent}>
          AI Agent Builder
        </FeatureRow>
        {data?.promptlabel_ids?.length > 0 && (
          <FeatureRow current={isCurrent}>
            {data.promptlabel_ids.length} AI Agent Templates
          </FeatureRow>
        )}
        <FeatureRow current={isCurrent}>
          Workspaces:{" "}
          {parseInt(data?.additional_accounts) > 0
            ? parseInt(data?.additional_accounts)
            : 1}
        </FeatureRow>
        <FeatureRow
          enabled={Boolean(parseInt(data?.daily_token_usage))}
          current={isCurrent}
        >
          Monthly AI Tasks:{" "}
          {parseInt(data?.daily_token_usage) > 0
            ? data?.daily_token_usage
            : "Unlimited"}
        </FeatureRow>
      </ul>

      <button
        type="button"
        onClick={handleCtaClick}
        className={
          isCurrent
            ? "w-full h-10 flex items-center justify-center text-[11px] font-bold rounded-lg bg-[hsl(var(--task-accent)/0.10)] text-[hsl(var(--task-accent))] border border-[hsl(var(--task-accent)/0.25)] uppercase tracking-[0.14em]"
            : "w-full h-10 flex items-center justify-center text-[11px] font-semibold rounded-lg border border-border bg-background text-foreground hover:bg-muted/60 transition-colors"
        }
      >
        {loading ? "wait... " : data?.buttonLabel}
      </button>

      {openConfirmModal && (
        <CommonModal
          isOpen={openConfirmModal}
          onClose={() => setOpenConfirmModal(false)}
          showAlert={false}
          title=""
        >
          <UpgradePlanModal
            price_id={
              planType === "monthly" ? data?.monthly_id : data?.yearly_id
            }
            plan={data}
            onClose={() => setOpenConfirmModal(false)}
            paymentMethod_id={paymentMethod_id}
            agency={agency}
          />
        </CommonModal>
      )}
      <Downgrade
        isOpen={openDowngradeModal}
        onClose={() => setDowngradeModal(false)}
        agency={agency}
        price_id={planType === "monthly" ? data?.monthly_id : data?.yearly_id}
        plan={data}
        paymentMethod_id={paymentMethod_id}
      />
      {openContactInfoModel && <ContactToSupportModal isOpen={openContactInfoModel} onClose={() => setOpenContactInfoModel(false)} />}
    </div>
  );
};

export default PricingPlanCard;
